import React, { useState, useEffect, useMemo } from 'react';
import api from './api';
import { generatePlanningReport } from './utils/pdfGenerator';

const ReportDashboard = () => {
    const [kapans, setKapans] = useState([]);
    const [packets, setPackets] = useState([]);
    const [selectedKapan, setSelectedKapan] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadKapans();
    }, []);

    const loadKapans = async () => {
        try {
            const res = await api.get('/kapans');
            setKapans(res.data);
        } catch (err) { console.error(err); }
    };

    const handleSelectKapan = async (kapan) => {
        setSelectedKapan(kapan);
        setLoading(true);
        try {
            const res = await api.get(`/packets/${kapan._id}`);
            setPackets(res.data);
        } catch (err) { 
            console.error("Error loading packets", err);
            setPackets([]); // Safety fallback
        } finally {
            setLoading(false);
        }
    };

    // --- REAL-TIME CALCULATIONS (FIXED) ---
    const stats = useMemo(() => {
        // Defaults
        const roughWt = selectedKapan?.weight || 0;
        
        // If no Kapan selected or no packets, return 0 values (Don't return null!)
        if (!selectedKapan || packets.length === 0) {
            return { roughWt, totalPacketWt: 0, totalVal: 0, yieldPerc: 0 };
        }

        const totalPacketWt = packets.reduce((acc, p) => acc + (p.weight || 0), 0);
        const totalVal = packets.reduce((acc, p) => acc + (p.planningData?.calculatedTotalValue || 0), 0);
        const yieldPerc = roughWt > 0 ? ((totalPacketWt / roughWt) * 100).toFixed(2) : 0;

        return { roughWt, totalPacketWt, totalVal, yieldPerc };
    }, [selectedKapan, packets]);

    return (
        <div className="container-fluid mt-4 mb-5">
            <h3 className="fw-bold text-dark mb-4">📊 Kapan Summary & Reports</h3>

            <div className="row">
                {/* --- LEFT: KAPAN LIST --- */}
                <div className="col-md-3">
                    <div className="card shadow-sm h-100">
                        <div className="card-header bg-dark text-white fw-bold">Select Rough Stone</div>
                        <div className="list-group list-group-flush overflow-auto" style={{maxHeight: '80vh'}}>
                            {kapans.map(k => (
                                <button
                                    key={k._id}
                                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedKapan?._id === k._id ? 'active' : ''}`}
                                    onClick={() => handleSelectKapan(k)}
                                >
                                    <div>
                                        <strong>{k.kapanName}</strong>
                                        <div className="small opacity-75">{k.weight} cts</div>
                                    </div>
                                    <span className="badge bg-light text-dark rounded-pill">{k.totalPackets || 0}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* --- RIGHT: REPORT VIEW --- */}
                <div className="col-md-9">
                    {!selectedKapan ? (
                        <div className="alert alert-secondary text-center p-5">
                            <h4>👈 Select a Kapan to view the report</h4>
                        </div>
                    ) : (
                        <div>
                            {/* 1. SUMMARY CARDS */}
                            <div className="row mb-4">
                                <div className="col-md-3">
                                    <div className="card bg-primary text-white text-center p-3 shadow-sm">
                                        <h3>{stats.roughWt}</h3>
                                        <small>Rough Weight (ct)</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card bg-success text-white text-center p-3 shadow-sm">
                                        <h3>{stats.totalPacketWt.toFixed(2)}</h3>
                                        <small>Plan Weight (ct)</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card bg-info text-white text-center p-3 shadow-sm">
                                        <h3>{stats.yieldPerc}%</h3>
                                        <small>Recovery / Yield</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card bg-warning text-dark text-center p-3 shadow-sm">
                                        <h3>${stats.totalVal.toLocaleString()}</h3>
                                        <small>Est. Total Value</small>
                                    </div>
                                </div>
                            </div>

                            {/* 2. ACTION BAR */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="fw-bold">📄 Detailed Packet List ({packets.length})</h5>
                                <button 
                                    className="btn btn-outline-danger fw-bold"
                                    onClick={() => generatePlanningReport(selectedKapan, packets)} // <--- This calls the new function
                                    disabled={packets.length === 0}
                                >
                                    📥 Download Estimate Plan PDF
                                </button>
                            </div>

                            {/* 3. DATA TABLE */}
                            <div className="card shadow-sm">
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th>Packet ID</th>
                                                <th>Weight</th>
                                                <th>Status</th>
                                                <th>Est. Value</th>
                                                <th>Stones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr><td colSpan="5" className="text-center p-4">Loading packets...</td></tr>
                                            ) : packets.length === 0 ? (
                                                <tr><td colSpan="5" className="text-center p-4">No packets created yet. Go to Process Manager to create some.</td></tr>
                                            ) : packets.map(p => (
                                                <tr key={p._id}>
                                                    <td className="fw-bold text-primary">{p.fullPacketId || p.packetName}</td>
                                                    <td>{p.weight} ct</td>
                                                    <td>
                                                        <span className={`badge ${p.status === 'Polished' ? 'bg-success' : 'bg-secondary'}`}>
                                                            {p.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {p.planningData ? `$${p.planningData.calculatedTotalValue.toLocaleString()}` : '-'}
                                                    </td>
                                                    <td>
                                                        {p.planningData ? p.planningData.stones.length : 0}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReportDashboard;